import { dashboardInputEditorSource } from '../misc/dashboardInputEditorSource';
import { Alert, Button, Layout, Skeleton } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import LayoutHeader from '../../../components/LayoutHeader';
import { TestProjectDetails, TestProjectPackagesEditDialog, TestProjectRuns } from '.';
import TestProjectProgressSection from './TestProjectProgressSection';
import {
    TestProjectDashboardStore,
    TestProjectDataStore,
    TestProjectInputEditorStore,
    TestProjectWizardStore,
    TestProjectRunCompareStore
} from '../stores';
import { Utils } from '../../common/services/Utils';
import TestProjectBaseline from './dashboard/TestProjectBaseline';
import { TestProjectInputEditor } from './TestProjectInputEditor';

type Props = {
    store: TestProjectDashboardStore;
    testProjectDataStore: TestProjectDataStore;
    wizardStore: TestProjectWizardStore;
    inputEditorStore: TestProjectInputEditorStore;
    runCompareStore: TestProjectRunCompareStore;
};

const TestProjectDashboardPage: React.FC<Props> = ({
    store,
    testProjectDataStore,
    wizardStore,
    inputEditorStore,
    runCompareStore
}) => {
    React.useEffect(() => {
        testProjectDataStore.loadTestProject();
    }, [testProjectDataStore]);

    const getErrorsContent = () => {
        if (
            testProjectDataStore.testProject == null ||
            testProjectDataStore.testProject.errors == null ||
            testProjectDataStore.testProject.errors.length === 0
        ) {
            return null;
        }

        return (
            <Alert
                message="Error"
                description={testProjectDataStore.testProject.errors.join('\n')}
                type="error"
                showIcon
                style={{ marginBottom: '24px', whiteSpace: 'pre-wrap' }}
            />
        );
    };

    const getTestProjectButtons = () => {
        let buttons = [
            <Button
                key="test-projects-dashboard-go-bacl"
                data-id="button-go-to-list"
                className="light"
                size="large"
                onClick={store.goBackToList}
            >
                Go back
            </Button>,
            <Button
                key="test-projects-dashboard-run"
                data-id="button-run-test-project"
                type="primary"
                size="large"
                onClick={store.runTestProject}
                disabled={
                    store.projectIsLocked ||
                    !testProjectDataStore.testProject?.hasValidBaselines ||
                    !testProjectDataStore.testProject?.hasValidTopics
                }
                loading={store.projectIsLocked}
            >
                Run test
            </Button>
        ];

        if (testProjectDataStore.testProject?.isRunning) {
            buttons.splice(
                1,
                0,
                <Button
                    key="test-projects-dashboard-abort"
                    data-id="button-abort-test-project-run"
                    size="large"
                    danger
                    onClick={store.abortTestProjectRun}
                >
                    Abort test run
                </Button>
            );
        }

        if (getErrorsContent()) {
            buttons.splice(
                1,
                0,
                <Button
                    key="test-projects-dashboard-clear"
                    data-id="button-clear-errors"
                    size="large"
                    onClick={store.cleanTestProject}
                >
                    Clean Test Project
                </Button>
            );
        }

        return buttons;
    };

    return (
        <Layout className="screen-size test-project-dashboard" style={{ ...{ height: '100%', background: 'white' } }}>
            <LayoutHeader
                title={testProjectDataStore.testProject?.name || <Skeleton active paragraph={{ rows: 0 }} />}
                subtitle={Utils.getSubtitle(store.currentProject)}
                buttons={getTestProjectButtons()}
            />
            {testProjectDataStore.testProjectLoadError && (
                <Alert
                    type="error"
                    message={testProjectDataStore.testProjectLoadError}
                    action={<Button onClick={testProjectDataStore.loadTestProject}>Retry</Button>}
                />
            )}
            {getErrorsContent()}
            <Layout>
                <Layout.Sider theme="light">
                    <TestProjectDetails store={testProjectDataStore} />
                </Layout.Sider>
                <Layout.Content className="test-project-dashboard-content">
                    <TestProjectPackagesEditDialog store={wizardStore} testProjectDataStore={testProjectDataStore} />
                    <TestProjectInputEditor
                        store={inputEditorStore}
                        dataStore={testProjectDataStore}
                        onMapInputs={() => inputEditorStore.openMapping(dashboardInputEditorSource(store))}
                    />
                    <TestProjectBaseline
                        store={testProjectDataStore}
                        onGoToBaselines={store.goToBaselines}
                        wizardStore={wizardStore}
                        onEditInputs={() => inputEditorStore.openDirect(dashboardInputEditorSource(store))}
                        onMapInputs={() => inputEditorStore.openMapping(dashboardInputEditorSource(store))}
                        inputsDisabled={!testProjectDataStore.canUseProject || inputEditorStore.isSubmitting}
                    />
                    {testProjectDataStore.testProject?.isRunning && (
                        <TestProjectProgressSection store={testProjectDataStore} />
                    )}
                    <TestProjectRuns
                        store={store}
                        testProjectDataStore={testProjectDataStore}
                        runCompareStore={runCompareStore}
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
};

export default observer(TestProjectDashboardPage);
